/**
 * First Sale Tracker Store
 *
 * MobX singleton store managing all client-side state for the
 * First Sale Conversion Tracker page. Handles fetching, step
 * toggling, notes, reset, and the performance stat.
 */

import { makeAutoObservable, runInAction } from 'mobx'
import FirstSaleTrackerService from '../../shared/services/FirstSaleTracker.service'
import { TOTAL_STEPS } from './FirstSaleTracker.constants'

class FirstSaleTrackerStore {
  /** @type {Object|null} The active first_sale_tracker record. */
  tracker = null

  /** @type {Array} The 8 step rows, ordered by step_number. */
  steps = []

  /** @type {boolean} True while the initial page load is in progress. */
  loading = true

  /** @type {boolean} True while a step update or reset is in progress. */
  saving = false

  /** @type {string|null} Error message from the most recent failed fetch. */
  error = null

  /** @type {{ pct_first_week: number|null }} Performance stat. */
  stats = { pct_first_week: null }

  /**
   * Auto-detected first sale for Step 8 prompt.
   * Shape: { av_date: 'YYYY-MM-DD' } or null.
   * @type {{ av_date: string }|null}
   */
  detectedSale = null

  /**
   * Side quest state from the backend.
   * @type {{ totalPoints: number, earnedPoints: number, quests: Array }}
   */
  sideQuests = { totalPoints: 0, earnedPoints: 0, quests: [] }

  /** @type {boolean} Controls the reset confirmation modal visibility. */
  showResetConfirm = false

  /** @type {boolean} Whether the welcome banner has been dismissed. */
  welcomeDismissed = false

  /**
   * Tracks unsaved note edits by step number.
   * Used for the dirty-state navigation guard.
   * @type {Object<number, string>}
   */
  dirtyNotes = {}

  /** Per-step save status indicators. Maps step_number → 'saved'|'error'|null */
  saveStatus = {}

  /** Timer IDs for auto-clearing save status indicators. */
  _saveStatusTimers = {}

  constructor() {
    makeAutoObservable(this)
  }

  // ---------------------------------------------------------------------------
  // Computed
  // ---------------------------------------------------------------------------

  /** True when all 8 steps are completed. */
  get isComplete() {
    return this.steps.length === TOTAL_STEPS && this.steps.every(s => parseInt(s.completed) === 1)
  }

  /** Number of completed steps (0-8). */
  get completedCount() {
    return this.steps.filter(s => parseInt(s.completed) === 1).length
  }

  /** The step_number of the first incomplete step, or null if all done. */
  get activeStepNumber() {
    const firstIncomplete = this.steps.find(s => parseInt(s.completed) !== 1)
    return firstIncomplete ? parseInt(firstIncomplete.step_number) : null
  }

  /** True if any step has unsaved notes edits. */
  get hasDirtyNotes() {
    return Object.keys(this.dirtyNotes).length > 0
  }

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------

  /**
   * Fetch the agent's active tracker record, its steps, and the performance stat.
   * If no active tracker exists, the backend creates one automatically.
   */
  fetchTracker = async () => {
    this.loading = true
    this.error = null

    try {
      const result = await FirstSaleTrackerService.search()

      runInAction(() => {
        if (result && result.tracker) {
          this.tracker = result.tracker
          this.steps = result.steps || []
          this.stats = result.stats || { pct_first_week: null }
          this.detectedSale = result.detectedSale || null
          this.sideQuests = result.sideQuests || {
            totalPoints: 0,
            earnedPoints: 0,
            quests: [],
          }
        } else {
          this.error = 'Tracker data was unavailable. Please refresh.'
        }
      })
    } catch (ex) {
      console.error('[FirstSaleTrackerStore] fetchTracker error:', ex)
      runInAction(() => {
        this.error = 'Failed to load your tracker. Please try again.'
      })
    } finally {
      runInAction(() => {
        this.loading = false
      })
    }
  }

  /**
   * Toggle a step's completion status.
   * The backend enforces sequential order and handles cascade uncomplete.
   * @param {number} stepNumber - Step number (1-8)
   * @param {boolean} completed - Whether to mark complete or incomplete
   */
  toggleStep = async (stepNumber, completed) => {
    this.saving = true

    try {
      const result = await FirstSaleTrackerService.updateStep(stepNumber, {
        completed,
      })

      runInAction(() => {
        if (result && result.tracker) {
          this.tracker = result.tracker
          this.steps = result.steps || this.steps
        } else {
          this.saveStatus = { ...this.saveStatus, [stepNumber]: 'error' }
        }
      })
    } catch (ex) {
      console.error('[FirstSaleTrackerStore] toggleStep error:', ex)
      runInAction(() => {
        this.saveStatus = { ...this.saveStatus, [stepNumber]: 'error' }
      })
    } finally {
      runInAction(() => {
        this.saving = false
      })
    }
  }

  /**
   * Save notes for a specific step (called on textarea blur).
   * @param {number} stepNumber - Step number (1-8)
   * @param {string} notes - The note text to save
   */
  updateStepNotes = async (stepNumber, notes) => {
    // Clear dirty state for this step
    this.clearDirtyNote(stepNumber)
    this.saveStatus = { ...this.saveStatus, [stepNumber]: null }

    try {
      const result = await FirstSaleTrackerService.updateStep(stepNumber, {
        notes,
      })

      runInAction(() => {
        if (result && result.steps) {
          this.steps = result.steps
        }
        this.saveStatus = { ...this.saveStatus, [stepNumber]: 'saved' }
      })

      // Auto-clear the "saved" indicator after 2 seconds
      clearTimeout(this._saveStatusTimers[stepNumber])
      this._saveStatusTimers[stepNumber] = setTimeout(() => {
        runInAction(() => {
          this.saveStatus = { ...this.saveStatus, [stepNumber]: null }
        })
      }, 2000)
    } catch (ex) {
      console.error('[FirstSaleTrackerStore] updateStepNotes error:', ex)
      runInAction(() => {
        this.saveStatus = { ...this.saveStatus, [stepNumber]: 'error' }
      })
    }
  }

  /**
   * Track an unsaved note edit for the dirty-state navigation guard.
   * @param {number} stepNumber
   * @param {string} value
   */
  setDirtyNote = (stepNumber, value) => {
    this.dirtyNotes = { ...this.dirtyNotes, [stepNumber]: value }
  }

  /**
   * Remove a step from the dirty notes map (e.g., after saving).
   * @param {number} stepNumber
   */
  clearDirtyNote = stepNumber => {
    const updated = { ...this.dirtyNotes }
    delete updated[stepNumber]
    this.dirtyNotes = updated
  }

  /** Open the reset confirmation modal. */
  openResetConfirm = () => {
    this.showResetConfirm = true
  }

  /** Close the reset confirmation modal. */
  closeResetConfirm = () => {
    this.showResetConfirm = false
  }

  /**
   * Archive the current tracker and create a fresh one.
   * Notes and history are preserved in the archived record.
   */
  resetTracker = async () => {
    this.saving = true
    this.showResetConfirm = false

    try {
      const result = await FirstSaleTrackerService.reset({})

      runInAction(() => {
        if (result && result.tracker) {
          this.tracker = result.tracker
          this.steps = result.steps || []
          this.dirtyNotes = {}
          this.saveStatus = {}
          this.detectedSale = null
        } else {
          this.error = 'Failed to reset tracker. Please try again.'
        }
      })

      // Re-fetch to seed and evaluate side quests for the new tracker
      if (result && result.tracker) {
        await this.fetchTracker()
      }
    } catch (ex) {
      console.error('[FirstSaleTrackerStore] resetTracker error:', ex)
      runInAction(() => {
        this.error = 'Failed to reset tracker. Please try again.'
      })
    } finally {
      runInAction(() => {
        this.saving = false
      })
    }
  }

  /**
   * Manually claim a side quest (for manual detection_type quests).
   * @param {string} questKey
   */
  claimQuest = async questKey => {
    this.saving = true
    try {
      await FirstSaleTrackerService.claimQuest(questKey, {})
      await this.fetchTracker()
    } catch (ex) {
      console.error('[FirstSaleTrackerStore] claimQuest error:', ex)
    } finally {
      runInAction(() => {
        this.saving = false
      })
    }
  }

  /** Dismiss the welcome banner. */
  dismissWelcome = () => {
    this.welcomeDismissed = true
  }

  /** Toggle the reset confirmation modal. */
  setShowResetConfirm = value => {
    this.showResetConfirm = !!value
  }
}

export default new FirstSaleTrackerStore()
